import { LoadingButton } from '@mui/lab';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import PlacementFlow from 'components/private/Modals/operation/entry/PlacementFlow/PlacementFlow';
import ShoppingCartAddModal from 'components/private/Modals/operation/entry/ShoppingCartAddModal/ShoppingCartAddModal';
import FinishOperationModal from 'components/private/Modals/operation/FinishOperationModal';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import { getOperationStatus } from 'hooks/operation/useEntryOperationTableColumns';
import useEntryWorkspace from 'hooks/operation/useEntryWorkspace';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { updateQueryParams } from 'utils/updateQueryParams';

import ProductList from '../../ProductList';
import { EntryDock } from './EntryDock';
import { ShoppingCartDetail } from './ShoppingCartDetail';
import { WorkspaceSidebar } from './WorkspaceSidebar';

const SIDEBAR_SECTION = {
  DOCK: 'dock',
  CART: 'cart',
  PRODUCT_LIST: 'productList',
};

const EntryOperationWorkspace = () => {
  const { guid } = useParams();

  const [selectedSection, setSelectedSection] = useState({
    type: SIDEBAR_SECTION.DOCK,
  });
  const [expandedCarts, setExpandedCarts] = useState(true);
  const [isShoppingCartAddModalOpen, setIsShoppingCartAddModalOpen] =
    useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [placementProduct, setPlacementProduct] = useState(null);

  const {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    voucherType,
    refreshWorkspace,
    canMutate,
    canSubmitOperation,
    canSubmitVoucher,
    removeFromBasket,
    deleteBasket,
    runTask,
    submitOperation,
    submitVoucher,
  } = useEntryWorkspace(guid);

  const selectedCart = useMemo(() => {
    if (selectedSection.type !== SIDEBAR_SECTION.CART) return null;
    return workspace?.shoppingCartList?.find(
      cart => cart.guid === selectedSection.guid,
    );
  }, [selectedSection, workspace]);

  const sectionTitle = useMemo(() => {
    switch (selectedSection.type) {
      case SIDEBAR_SECTION.DOCK:
        return 'بارانداز';
      case SIDEBAR_SECTION.PRODUCT_LIST:
        return 'لیست محصول';
      case SIDEBAR_SECTION.CART:
        return selectedCart?.title || selectedSection.title || '';
      default:
        return '';
    }
  }, [selectedSection, selectedCart]);

  const handleSelectSidebarItem = (type, cart) => {
    switch (type) {
      case SIDEBAR_SECTION.DOCK:
        setSelectedSection({ type: SIDEBAR_SECTION.DOCK });
        updateQueryParams({
          selectedTab: SIDEBAR_SECTION.DOCK,
          selectedCart: null,
        });
        break;

      case SIDEBAR_SECTION.PRODUCT_LIST:
        setSelectedSection({ type: SIDEBAR_SECTION.PRODUCT_LIST });
        updateQueryParams({
          selectedTab: SIDEBAR_SECTION.PRODUCT_LIST,
          selectedCart: null,
        });
        break;

      case SIDEBAR_SECTION.CART:
        setSelectedSection({
          type: SIDEBAR_SECTION.CART,
          guid: cart.guid,
          title: cart.title,
        });
        setExpandedCarts(true);
        updateQueryParams({
          selectedTab: SIDEBAR_SECTION.CART,
          selectedCart: cart.guid,
        });
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const selectedTabParam = params.get('selectedTab');
    const selectedCartParam = params.get('selectedCart');

    if (!selectedTabParam) return;

    if (selectedTabParam === SIDEBAR_SECTION.CART) {
      if (selectedCartParam) {
        handleSelectSidebarItem(SIDEBAR_SECTION.CART, {
          guid: selectedCartParam,
        });
      }
      return;
    }

    handleSelectSidebarItem(selectedTabParam);
  }, []);

  const handleToggleCarts = () => {
    setExpandedCarts(prev => !prev);
  };

  const handleDeleteCart = async cartGuid => {
    const ok = await deleteBasket(cartGuid);
    if (ok) {
      setSelectedSection({ type: SIDEBAR_SECTION.DOCK });
      updateQueryParams({
        selectedTab: SIDEBAR_SECTION.DOCK,
        selectedCart: null,
      });
    }
  };

  const handleConfirmFinishOperation = async () => {
    const ok = await submitOperation();
    if (ok) setIsFinishModalOpen(false);
  };

  const status = getOperationStatus({ state });

  const renderMainContent = () => {
    if (loading && !workspace) return <SpinnerLoader />;
    if (!workspace) {
      return (
        <Typography color="text.secondary" textAlign="center" py={4}>
          اطلاعات فضای کاری یافت نشد.
        </Typography>
      );
    }

    switch (selectedSection.type) {
      case SIDEBAR_SECTION.DOCK:
        return (
          <EntryDock
            workspace={workspace}
            canMutate={canMutate}
            onPlaceProduct={setPlacementProduct}
          />
        );

      case SIDEBAR_SECTION.PRODUCT_LIST:
        return (
          <ProductList
            operationType="entry"
            operationGuid={workspace.guid}
            state={state}
            voucherType={voucherType}
          />
        );

      case SIDEBAR_SECTION.CART:
        return selectedCart ? (
          <ShoppingCartDetail
            cart={selectedCart}
            canMutate={canMutate}
            actionLoading={actionLoading}
            onRemoveProduct={removeFromBasket}
            onRunTask={runTask}
            onDeleteCart={handleDeleteCart}
          />
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            سبد انتخاب‌شده یافت نشد.
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <RequireOperationSettings>
      {/* operation header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: 'full',
          mt: 2,
        }}
      >
        {/* page title and status of operation */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
            عملیات ورود &quot;{workspace?.title}&quot;
          </Typography>
          {<Chip size="small" color={status.color} label={status.label} />}
        </Box>

        {/* action buttons */}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {canSubmitVoucher ? (
            <LoadingButton
              variant="contained"
              size="small"
              color="success"
              loading={loading || actionLoading}
              disabled={loading || actionLoading}
              onClick={submitVoucher}
            >
              ثبت سند
            </LoadingButton>
          ) : null}

          {canSubmitOperation ? (
            <LoadingButton
              variant="contained"
              color="warning"
              loading={loading || actionLoading}
              disabled={loading || actionLoading}
              onClick={() => setIsFinishModalOpen(true)}
            >
              اتمام عملیات
            </LoadingButton>
          ) : null}
        </Stack>
      </Box>

      <Divider orientation="horizontal" variant="fullWidth" sx={{ my: 2 }} />

      {/* operation body */}
      <Box sx={{ display: 'flex', height: '85vh', gap: 2 }}>
        <WorkspaceSidebar
          workspace={workspace}
          expandedCarts={expandedCarts}
          selectedSection={selectedSection}
          onSelectSidebarItem={handleSelectSidebarItem}
          onToggleCarts={handleToggleCarts}
          onAddNewShoppingCart={() => setIsShoppingCartAddModalOpen(true)}
          canAddCart={canMutate}
        />

        <Divider orientation="vertical" variant="fullWidth" flexItem />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: {
              xs: '2',
              sm: '3',
              md: '4',
              lg: '5',
            },
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
              {sectionTitle}{' '}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {renderMainContent()}
          </Box>
        </Box>
      </Box>

      {/* modals */}
      <ShoppingCartAddModal
        open={isShoppingCartAddModalOpen}
        onClose={() => setIsShoppingCartAddModalOpen(false)}
        operationGuid={guid}
        onCompleted={refreshWorkspace}
      />

      <FinishOperationModal
        open={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        onConfirm={handleConfirmFinishOperation}
        loading={actionLoading}
        title={title}
      />

      <PlacementFlow
        open={Boolean(placementProduct)}
        onClose={() => setPlacementProduct(null)}
        operationGuid={guid}
        product={placementProduct}
        onCompleted={refreshWorkspace}
      />
    </RequireOperationSettings>
  );
};

export default EntryOperationWorkspace;
